/* eslint-disable no-debugger */
import prepareBreadcrumbStructuredPageData from "@insite/client-framework/Common/Utilities/prepareBreadcrumbStructuredPageData";
import { getUrl } from "@insite/client-framework/ServerSideRendering";
import { Website } from "@insite/client-framework/Services/WebsiteService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedBrandPath, getSelectedProductPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBrandStateByPath } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getComputedVariantProduct,
    getProductState,
    getProductStateByPath,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { generateLinksFrom } from "@insite/content-library/Components/PageBreadcrumbs";
import * as React from "react";
import { useEffect } from "react";
import { connect } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const { pathname } = location;
    const currentPageData = getCurrentPage(state);
    let productPath =
        getSelectedProductPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    let queryString = location.search;
    if (location.pathname.toLowerCase().startsWith("/content/page/") && productPath.indexOf("?") > -1) {
        queryString = productPath ? productPath.substring(productPath.indexOf("?")) : "";
        productPath = productPath.substring(0, productPath.indexOf("?"));
    }
    const catalogPageState = getCatalogPageStateByPath(state, productPath);
    const productState = getProductStateByPath(state, productPath);
    const brandPath = getSelectedBrandPath(state) || pathname;
    const brandState = getBrandStateByPath(state, brandPath);

    const catalogOgImage = catalogPageState.value?.openGraphImage || catalogPageState.value?.primaryImagePath;
    const productdetailOgImage =
        productState.value?.mediumImagePath || productState.value?.largeImagePath || productState.value?.smallImagePath;
    const brandOgImage = brandState.value?.logoSmallImagePath || brandState.value?.logoLargeImagePath;
    const variantProductState = getProductState(state, state.pages.productDetails.selectedProductId);
    const computedVariantProduct = getComputedVariantProduct(productState, variantProductState);
    return {
        breadcrumbLinks: state.components.breadcrumbs.links,
        linksState: state.links,
        nodeId: getCurrentPage(state).nodeId,
        homePageUrl: getHomePageUrl(state),
        enableStructuredData:
            state.context?.settings?.settingsCollection?.websiteSettings?.enableOrganizationStructuredData,
        website: state.context?.website,
        catalogOgImage,
        productdetailOgImage,
        brandOgImage,
        location,
        currentPageData,
        productState: computedVariantProduct,
        websiteName: state.context.website.name,
        currentLanguage: state.context.session.language,
    };
};

type Props = ReturnType<typeof mapStateToProps>;

const TECCustomSchema: React.FC<Props> = (props: Props) => {
    const {
        breadcrumbLinks,
        linksState,
        location,
        enableStructuredData,
        website,
        nodeId,
        homePageUrl,
        catalogOgImage,
        productdetailOgImage,
        brandOgImage,
        currentPageData,
        productState: { value: product },
        websiteName,
        currentLanguage,
    } = props;

    if (typeof window === "undefined" && typeof document === "undefined") {
        return null; // Prevent execution on the server side
    }

    const { fields } = currentPageData;
    const links = breadcrumbLinks || generateLinksFrom(linksState, nodeId, homePageUrl);
    const windowsOrigin = window?.location?.origin;
    const pathname = location.pathname;
    const isAboutPage = pathname.includes("/AboutUs");
    const isBlogPage = pathname.includes("/blog");
    const isContactPage = pathname.includes("/contact");
    const href = window?.location?.href;
    const isHomepage = currentPageData?.type === "HomePage";
    const isBreadCrumbPresent = document.getElementById("breadcrumb-structured-data");
    const isPDP = currentPageData?.type === "ProductDetailsPage";
    const isProductListPage = currentPageData?.type === "ProductListPage";
    const isBrandPage = currentPageData?.type === "BrandPage" || currentPageData?.type === "BrandDetailsPage";

    if (isPDP && (!breadcrumbLinks || breadcrumbLinks.length === 0)) {
        return null;
    }

    const prepareStructuredPageData = (website: Website, href: string) => {
        const url: { protocol: string; host: string } = IS_SERVER_SIDE ? getUrl()! : window.location;
        const authority = `${url.protocol}//${url.host}`;
        const data = {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: website.companyName,
            address: {
                "@type": "PostalAddress",
                streetAddress: [website.address1, website.address2].filter(o => o).join(", "),
                addressLocality: website.city,
                addressRegion: website.state,
                postalCode: website.postalCode,
                addressCountry: website.companyCountry,
            },
            telephone: website.companyPhone,
            url: href,
            logo:
                website.companyLogoImagePath &&
                (website.companyLogoImagePath.indexOf("http") === 0
                    ? website.companyLogoImagePath
                    : authority + website.companyLogoImagePath),
            sameAs: website.companySocialAccounts ? website.companySocialAccounts.split(",") : [],
        } as any;
        return JSON.stringify(data);
    };
    const structuredData =
        (isAboutPage || isContactPage) && !isBlogPage && enableStructuredData && website
            ? prepareStructuredPageData(website, href) // Ensure this function returns valid JSON-LD data
            : null;

    const breadCrumbsStructures =
        !isHomepage && !isBreadCrumbPresent && enableStructuredData && website
            ? prepareBreadcrumbStructuredPageData(links)
            : null;

    const removeExistingScripts = () => {
        const existingScripts = [
            document.getElementById("about-structured-data"),
            document.getElementById("breadcrumbAbout-structured-data"),
        ];
        existingScripts.forEach(script => script && document.head.removeChild(script));
    };

    const appendScript = (id: string, data: string) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = id;
        script.innerHTML = data;
        document.head.appendChild(script);
    };

    const setMetaTag = (name: string, content: string) => {
        let tag = document.querySelector(
            name.includes("twitter") ? `meta[name='${name}']` : `meta[property='${name}']`,
        );
        if (!tag) {
            tag = document.createElement("meta");
            name.includes("twitter") ? tag.setAttribute("name", name) : tag.setAttribute("property", name);
            document.head.appendChild(tag);
        }
        tag.setAttribute("content", content);
    };

    useEffect(() => {
        // Setting Twitter meta tags dynamically
        const title = document.title;
        let description = document.querySelector('meta[name="description"]');
        const ogImage = document.querySelector('meta[property="og:image"]');
        const imgUrl =
            fields["openGraphImage"] || isPDP
                ? productdetailOgImage || `${windowsOrigin}/userfiles/ogimage/ogImage.png`
                : isBrandPage && brandOgImage
                  ? `${windowsOrigin}${brandOgImage}`
                  : ogImage?.getAttribute("content") ||
                    catalogOgImage ||
                    productdetailOgImage ||
                    `${windowsOrigin}/userfiles/ogimage/ogImage.png`;

        // Update document.title if fields?.overrideSeoTitle is present
        const metaTitle = fields?.overrideSeoTitle || title;
        if (fields?.overrideSeoTitle) {
            document.title = metaTitle; // Update the page title
        }

        setMetaTag("twitter:card", "summary_large_image");
        setMetaTag("twitter:title", metaTitle);
        setMetaTag("twitter:description", description?.getAttribute("content") || "");
        setMetaTag("twitter:image", imgUrl);
        setMetaTag("twitter:url", href);
        setMetaTag("og:title", metaTitle);
        setMetaTag("og:description", description?.getAttribute("content") || "");
        !isProductListPage && setMetaTag("og:image", imgUrl);
        setMetaTag("og:url", href);
        setMetaTag("og:type", isPDP ? "product" : "website");
        setMetaTag("twitter:site", `@${website.name}`);

        // Clean up function to remove meta tags when component is unmounted
        return () => {
            document
                .querySelectorAll("meta[name^='twitter:'], meta[property^='og:']")
                .forEach(tag => document.head.removeChild(tag));
        };
    }, [location, catalogOgImage, productdetailOgImage, brandOgImage, breadcrumbLinks]);

    useEffect(() => {
        removeExistingScripts();
        if ((!breadcrumbLinks || breadcrumbLinks.length === 0) && (!links || links.length === 0)) {
            return;
        }

        if (!website || !enableStructuredData) {
            return;
        }
        // Prepare breadcrumb structured data for PDP and other pages
        const pdpBreadCrumbStructure = prepareBreadcrumbStructuredPageData(links);

        // Append organization structured data if available
        if (structuredData?.trim()) {
            appendScript("about-structured-data", structuredData);
        }

        // Append breadcrumb structured data
        if (isPDP && pdpBreadCrumbStructure) {
            // Delay appending for PDP to ensure breadcrumbs are ready
            setTimeout(() => {
                appendScript("breadcrumbAbout-structured-data", pdpBreadCrumbStructure);
            }, 500);
        } else if (breadCrumbsStructures?.trim()) {
            appendScript("breadcrumbAbout-structured-data", breadCrumbsStructures);
        }

        return () => {
            removeExistingScripts();
        };
    }, [breadcrumbLinks, links]);

    useEffect(() => {
        const existingEmptyScript = document.querySelector('script[type="application/ld+json"]');
        if (existingEmptyScript && existingEmptyScript.innerHTML.trim() === "") {
            existingEmptyScript.remove();
        }

        const languageRegion = "en-us";
        const linkTag = document.createElement("link");
        linkTag.rel = "alternate";
        linkTag.hreflang = currentLanguage?.languageCode.toLowerCase() || languageRegion;
        linkTag.href = currentLanguage?.languageCode && currentLanguage?.languageCode.toLowerCase() !== "en-us" ?  `${href}?SetContextLanguageCode=${linkTag.hreflang}` : href;

        const existingLink = document.head.querySelector(`link[rel="alternate"][hreflang="${currentLanguage?.languageCode.toLowerCase() || languageRegion}"]`);
        if (existingLink) {
            document.head.removeChild(existingLink);
        }

        document.head.appendChild(linkTag);

        return () => {
            if (document.head.contains(linkTag)) {
                document.head.removeChild(linkTag);
            }
        };
    }, [location, href]);


    return null;
};

const definition: WidgetDefinition = {
    group: "Basic",
    displayName: "Custom Schema",
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(TECCustomSchema),
    definition,
};

export default widgetModule;
